/* ==========================================================================
   Kheer Prasadam  ·  assets/kheer-prasadam.js
   --------------------------------------------------------------------------
   • Raw product  → AJAX add-to-cart via native /cart/add.js, integrates with
     the theme cart drawer (publish cartUpdate BEFORE renderContents to avoid
     the drawer-form-drop bug).
   • Prepared product → "Book a Visit" popup → POSTs the lead to YOUR PHP
     endpoint, which sends the WATI "kheer_loni" template. The WATI token lives
     only in PHP, never here.

   Vanilla JS, no libraries, scoped to <kheer-prasadam>.
   ========================================================================== */

if (!customElements.get('kheer-prasadam')) {
  customElements.define(
    'kheer-prasadam',
    class KheerPrasadam extends HTMLElement {
      constructor() {
        super();
        this._onClick = this._onClick.bind(this);
        this._onKeydown = this._onKeydown.bind(this);
      }

      connectedCallback() {
        this.endpoint = this.dataset.endpoint || '';
        this.sectionId = this.dataset.sectionId;
        this.modal = this.querySelector('[data-kp-modal]');
        this.form = this.querySelector('[data-kp-form]');
        this.status = this.querySelector('[data-kp-status]');
        this.successMessage = this._getSuccessMessage();

        this.addEventListener('click', this._onClick);
        if (this.form) this.form.addEventListener('submit', (e) => this._onFormSubmit(e));

        this._initCountdown();
      }

      disconnectedCallback() {
        this.removeEventListener('click', this._onClick);
        document.removeEventListener('keydown', this._onKeydown);
        if (this._cdTimer) clearInterval(this._cdTimer);
      }

      /* ---------------- Urgency countdown ---------------- */
      _initCountdown() {
        const el = this.querySelector('[data-kp-countdown]');
        if (!el) return;

        const deadline = new Date(el.dataset.deadline).getTime();
        if (isNaN(deadline)) {
          el.hidden = true;
          return;
        }

        const timer = el.querySelector('.kp-countdown__timer');
        const dEl = el.querySelector('[data-days]');
        const hEl = el.querySelector('[data-hours]');
        const mEl = el.querySelector('[data-mins]');
        const sEl = el.querySelector('[data-secs]');
        const pad = (n) => String(n).padStart(2, '0');

        const tick = () => {
          const diff = deadline - Date.now();
          if (diff <= 0) {
            clearInterval(this._cdTimer);
            const msg = el.dataset.expired || 'Booking is now closed.';
            if (timer) timer.innerHTML = '<p class="kp-countdown__closed">' + msg + '</p>';
            return;
          }
          const d = Math.floor(diff / 86400000);
          const h = Math.floor((diff % 86400000) / 3600000);
          const m = Math.floor((diff % 3600000) / 60000);
          const s = Math.floor((diff % 60000) / 1000);
          if (dEl) dEl.textContent = pad(d);
          if (hEl) hEl.textContent = pad(h);
          if (mEl) mEl.textContent = pad(m);
          if (sEl) sEl.textContent = pad(s);
        };

        tick();
        this._cdTimer = setInterval(tick, 1000);
      }

      _getSuccessMessage() {
        // Read from a data attribute if present, else a sensible default.
        return (
          this.getAttribute('data-success') ||
          "Thank you! Your visit is booked — check WhatsApp for confirmation."
        );
      }

      /* ---------------- Delegated clicks ---------------- */
      _onClick(event) {
        const minus = event.target.closest('[data-kp-qty-minus]');
        if (minus) {
          event.preventDefault();
          this._stepQty(minus, -1);
          return;
        }
        const plus = event.target.closest('[data-kp-qty-plus]');
        if (plus) {
          event.preventDefault();
          this._stepQty(plus, 1);
          return;
        }
        if (event.target.closest('[data-kp-add]')) {
          event.preventDefault();
          this._addToCart(event.target.closest('[data-kp-add]'));
          return;
        }
        if (event.target.closest('[data-kp-book-open]')) {
          event.preventDefault();
          this._openModal(event.target.closest('[data-kp-book-open]'));
          return;
        }
        if (event.target.closest('[data-kp-close]')) {
          event.preventDefault();
          this._closeModal();
        }
      }

      /* ---------------- Quantity stepper ---------------- */
      _qtyInputFor(el) {
        const scope = el.closest('.kp-card') || this;
        return scope.querySelector('[data-kp-qty-input]');
      }

      _stepQty(button, direction) {
        const input = this._qtyInputFor(button);
        if (!input) return;
        const min = parseInt(input.min, 10) || 1;
        const current = parseInt(input.value, 10) || min;
        input.value = Math.max(min, current + direction);
      }

      /* ---------------- Add to cart (raw product) ---------------- */
      _addToCart(button) {
        if (button.dataset.loading === 'true' || button.hasAttribute('disabled')) return;
        const variantId = button.dataset.variantId;
        if (!variantId) return;

        // Read the quantity from the stepper (defaults to 1).
        const qtyInput = this._qtyInputFor(button);
        let quantity = qtyInput ? parseInt(qtyInput.value, 10) : 1;
        if (!quantity || quantity < 1) quantity = 1;

        button.dataset.loading = 'true';
        button.classList.add('is-loading');
        button.setAttribute('aria-disabled', 'true');

        const cart =
          document.querySelector('cart-drawer') ||
          document.querySelector('cart-notification');

        const formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', quantity);
        if (cart && typeof cart.getSectionsToRender === 'function') {
          formData.append('sections', cart.getSectionsToRender().map((s) => s.id));
          formData.append('sections_url', window.location.pathname);
          if (typeof cart.setActiveElement === 'function') {
            cart.setActiveElement(document.activeElement);
          }
        }

        const addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add.js';

        fetch(addUrl, {
          method: 'POST',
          headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
          body: formData,
        })
          .then((r) => r.json())
          .then((data) => {
            if (data.status) return; // sold out / error
            // Broadcast FIRST, then render drawer LAST (prevents the drawer
            // losing #CartDrawer-Form and breaking its Check out button).
            try {
              if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
                publish(PUB_SUB_EVENTS.cartUpdate, {
                  source: 'kheer-prasadam',
                  productVariantId: variantId,
                });
              }
            } catch (e) {
              /* non-fatal */
            }
            document.dispatchEvent(
              new CustomEvent('kp:item-added', { bubbles: true, detail: { variantId } })
            );
            if (cart && typeof cart.renderContents === 'function') {
              cart.renderContents(data);
              if (cart.classList && cart.classList.contains('is-empty')) {
                cart.classList.remove('is-empty');
              }
            }
          })
          .catch(() => {})
          .finally(() => {
            button.dataset.loading = 'false';
            button.classList.remove('is-loading');
            button.removeAttribute('aria-disabled');
          });
      }

      /* ---------------- Modal ---------------- */
      _openModal(opener) {
        if (!this.modal) return;
        this._opener = opener;
        this.modal.hidden = false;
        document.body.classList.add('kp-modal-open');
        document.addEventListener('keydown', this._onKeydown);
        // Focus the first field for accessibility.
        const first = this.modal.querySelector('input, button');
        if (first) first.focus();
      }

      _closeModal() {
        if (!this.modal || this.modal.hidden) return;
        this.modal.hidden = true;
        document.body.classList.remove('kp-modal-open');
        document.removeEventListener('keydown', this._onKeydown);
        if (this._opener) this._opener.focus();
      }

      _onKeydown(event) {
        if (event.key === 'Escape') {
          this._closeModal();
          return;
        }
        if (event.key === 'Tab') this._trapFocus(event);
      }

      _trapFocus(event) {
        const focusables = this.modal.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        if (!focusables.length) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }

      /* ---------------- Book a Visit submit → PHP → WATI ---------------- */
      _onFormSubmit(event) {
        event.preventDefault();
        const submit = this.form.querySelector('[data-kp-submit]');
        const label = this.form.querySelector('[data-kp-submit-label]');

        const nameEl = this.form.querySelector('[name="name"]');
        const phoneEl = this.form.querySelector('[name="phone"]');
        const dateEl = this.form.querySelector('[name="date"]');
        const emailEl = this.form.querySelector('[name="email"]');

        const name = nameEl.value.trim();
        const phoneRaw = phoneEl.value.trim();
        const phoneDigits = phoneRaw.replace(/\D/g, '');

        // Basic validation (icon + message, not colour alone).
        if (!name) {
          return this._invalid(nameEl, 'Please enter your name.');
        }
        if (phoneDigits.length < 10) {
          return this._invalid(phoneEl, 'Please enter a valid WhatsApp number.');
        }
        nameEl.removeAttribute('aria-invalid');
        phoneEl.removeAttribute('aria-invalid');

        if (!this.endpoint) {
          return this._setStatus('Booking is not configured yet. Please contact us.', 'error');
        }

        submit.disabled = true;
        submit.classList.add('is-loading');
        this._setStatus('', null, true);

        fetch(this.endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: name,
            phone: phoneDigits,
            date: dateEl ? dateEl.value : '',
            email: emailEl ? emailEl.value.trim() : '',
            page: window.location.href,
          }),
        })
          .then((r) => r.json().catch(() => ({ success: false })))
          .then((res) => {
            if (res && res.success) {
              this._setStatus(this.successMessage, 'success');
              this.form.reset();
              document.dispatchEvent(
                new CustomEvent('kp:lead-booked', { bubbles: true, detail: { name, phone: phoneDigits } })
              );
              setTimeout(() => this._closeModal(), 2500);
            } else {
              this._setStatus(
                (res && res.message) || 'Something went wrong. Please try again.',
                'error'
              );
            }
          })
          .catch(() => {
            this._setStatus('Network error. Please try again.', 'error');
          })
          .finally(() => {
            submit.disabled = false;
            submit.classList.remove('is-loading');
          });
      }

      _invalid(field, message) {
        field.setAttribute('aria-invalid', 'true');
        field.focus();
        this._setStatus(message, 'error');
      }

      _setStatus(message, type, hide) {
        if (!this.status) return;
        if (hide) {
          this.status.hidden = true;
          return;
        }
        this.status.textContent = message;
        this.status.classList.remove('is-success', 'is-error');
        if (type) this.status.classList.add('is-' + type);
        this.status.hidden = false;
      }
    }
  );
}
